import type { PhotoKey } from './imagery';

/**
 * Planning guides for the Inspiration section — Aghosha's own editorial
 * content (not venue data). Each article is practical advice a guest can use
 * before booking. Written in English with Malayalam titles and summaries; a
 * Malayalam reader gets the English body until a translated one is added.
 *
 * `body` is a list of blocks so the article page can lay out headings, prose,
 * lists and tips without parsing markup.
 *
 * Keep the advice general and true: no invented figures, prices or quotes, and
 * mention Aghosha only for what it actually does (search by city and guest
 * count, show free sessions, compare, send a request the venue confirms, pay
 * the venue directly — nothing is paid online).
 */
export type ArticleBlock =
  | { h: string }
  | { p: string }
  | { list: string[] }
  | { tip: string };

export interface Article {
  slug: string;
  category: 'planning' | 'weddings' | 'corporate' | 'venues';
  photo: PhotoKey;
  readMins: number;
  title: { en: string; ml: string };
  excerpt: { en: string; ml: string };
  body: ArticleBlock[];
}

export const ARTICLES: Article[] = [
  {
    slug: 'choose-the-right-auditorium',
    category: 'venues',
    photo: 'auditoriumRed',
    readMins: 3,
    title: { en: 'How to choose the right auditorium', ml: 'ശരിയായ ഓഡിറ്റോറിയം എങ്ങനെ തിരഞ്ഞെടുക്കാം' },
    excerpt: {
      en: 'Capacity, location, parking and the hidden costs worth checking before you shortlist a venue.',
      ml: 'ഒരു വേദി തിരഞ്ഞെടുക്കും മുൻപ് ശേഷി, സ്ഥലം, പാർക്കിംഗ്, മറഞ്ഞിരിക്കുന്ന ചെലവുകൾ എന്നിവ പരിശോധിക്കുക.',
    },
    body: [
      {
        p: 'Most venue decisions are made on photographs, and most venue regrets come from things a photograph cannot show: a car park that fills by ten, a generator that runs the lights but not the air conditioning, a dining hall that seats half the guests at once. A shortlist built on a few practical questions saves you from all three.',
      },
      { h: 'Start with the guest count, then add a margin' },
      {
        p: 'Write down a realistic number, not the invitation count. For a wedding or a family function, think about how many people will be inside the hall at the busiest moment. For a conference, use confirmed registrations plus a small allowance for walk-ins.',
      },
      {
        p: 'Then compare it with two numbers. Seated capacity is how many chairs the hall holds in its usual layout. Floating capacity is how many people can move through over the day, which matters for receptions and open houses where guests come and go. A hall that is too big feels empty in photographs; one that is too small becomes uncomfortable within an hour.',
      },
      { h: 'Location is about your guests, not just you' },
      {
        p: 'A venue next to your home is convenient for you, but ask where most of your guests are coming from. Check how far the hall is from the nearest bus stand or railway station, whether the approach road can take a tourist bus, and how easy the entrance is for elderly relatives.',
      },
      { h: 'Parking decides the mood at the gate' },
      {
        list: [
          'How many cars the venue can park on its own land, and where the overflow goes.',
          'Separate space for two-wheelers, which often outnumber cars at Kerala functions.',
          'A drop-off point at the door, so elders and the families of the couple do not walk from the road.',
          'Whether the venue provides someone to direct parking on the day.',
        ],
      },
      { h: 'Facilities that matter on the day' },
      {
        list: [
          'Air conditioning in the main hall and, if you need it, the dining area.',
          'Power backup that covers air conditioning, lights and sound, not just a few fans.',
          'A lift or ramp if the hall is above the ground floor.',
          'Enough toilets for your guest count, kept clean through the event.',
          'Changing rooms or green rooms near the stage.',
          'Drinking water points that guests can find without asking.',
        ],
      },
      { h: 'The costs that are not on the first quote' },
      {
        p: 'The hall rent is rarely the whole bill. Before you compare two venues on price, ask each one the same questions: what happens if the event runs past the session, whether generator fuel, cleaning or electricity is charged separately, whether decoration must be done by an approved decorator, and whether you are required to use the in-house caterer. A cheaper hall with a mandatory caterer can end up costing more than a dearer one that lets you choose.',
      },
      {
        tip: 'On Aghosha, filter by city and guest count first, then open the venues that fit and check which sessions are free on your date. Comparing two halls side by side makes the differences in capacity, facilities and price easy to see.',
      },
      { h: 'Visit before you commit' },
      {
        p: 'Go at the same time of day as your event if you can. You will see how the light falls on the stage, how warm the hall gets, and how busy the road is. Walk the route a guest would take from parking to seat, and from seat to dining hall. If something feels awkward to you on an empty day, it will feel worse with a full house.',
      },
    ],
  },
  {
    slug: 'wedding-hall-setup',
    category: 'weddings',
    photo: 'weddingAisle',
    readMins: 3,
    title: { en: 'Setting up a hall for a wedding', ml: 'വിവാഹത്തിനായി ഹാൾ എങ്ങനെ ഒരുക്കാം' },
    excerpt: {
      en: 'Stage, seating, dining and flow — how to lay out a wedding hall so guests, family and photographers all have room.',
      ml: 'വേദി, ഇരിപ്പിടം, ഭക്ഷണം — അതിഥികൾക്കും കുടുംബത്തിനും ഫോട്ടോഗ്രാഫർമാർക്കും ഇടമുള്ള രീതിയിൽ ഹാൾ ഒരുക്കാം.',
    },
    body: [
      {
        p: 'A wedding hall has to work for several groups at once: the couple and their families on the stage, guests who want to see the ceremony, photographers who need room to move, and the catering team serving a meal on time. Good setup is mostly about giving each of them their own space.',
      },
      { h: 'Plan the day around the ceremony time' },
      {
        p: 'Whether it is a muhurtham fixed by the family, a nikah, or a reception after a church service, the ceremony time sets everything else. Book a session that starts early enough for decorators to finish before guests arrive, and ask the venue exactly when you can get in. A morning muhurtham often means decoration the evening before, which may need an extra session.',
      },
      { h: 'The stage' },
      {
        list: [
          'Size it for everyone who will stand on it during the ceremony, plus the photographers who work close in.',
          'Keep a clear, uncluttered backdrop behind the couple. It is in almost every photograph.',
          'Make sure the steps are easy for elders, with a hand rail if possible.',
          'Check the stage lighting. Bright, even light on faces matters more than coloured effects.',
          'Leave a strip of floor in front of the stage free for the video team, so they are not standing in the aisle.',
        ],
      },
      { h: 'Seating and flow' },
      {
        p: 'Reserve the first rows for close family on both sides and mark them clearly. Keep a wide central aisle, and a second route along the side so guests can come up to bless the couple without crossing the ceremony. Put a few chairs near the entrance for elders who arrive late or need to leave early, and check that a wheelchair can reach the front.',
      },
      { h: 'The sadya and dining' },
      {
        p: 'For most Kerala weddings the meal is as important as the ceremony. Ask how many guests the dining hall seats at one time, because that decides how many batches you need and how long the last guests wait. A dining hall on a different floor needs a lift or a gentle staircase, and a clear sign from the main hall.',
      },
      {
        list: [
          'Confirm whether you must use the venue caterer or may bring your own.',
          'Ask where the serving staff will plate or serve, and where the wash area is.',
          'Check that there is enough drinking water at every table.',
          'Agree when the first batch will be seated, and tell the ceremony team.',
        ],
      },
      {
        tip: 'Ask someone from the family to be the single contact for the venue and the caterer on the day. Decisions about the next batch, the stage lights or a late arrival go much faster with one person in charge.',
      },
      { h: 'Rooms, power and the small things' },
      {
        p: 'A bridal room close to the stage lets the bride rest and change between the ceremony and the reception. A second room for the groom and family is just as useful. Ask if the power backup runs the whole hall, including air conditioning and the sound system. If there will be chenda melam or nadaswaram at the entrance, check there is space for the players and their instruments.',
      },
      { h: 'If the wedding is in the monsoon' },
      {
        p: 'Between June and September, rain can arrive with little warning. Look for a covered drop-off point and a covered path from parking to the hall, find out whether the parking area turns muddy, and keep umbrellas and floor mats at the entrance. Photographs planned for the garden need an indoor backup spot.',
      },
    ],
  },
  {
    slug: 'before-you-book-checklist',
    category: 'planning',
    photo: 'planningNotes',
    readMins: 3,
    title: { en: 'Things to check before booking a venue', ml: 'വേദി ബുക്ക് ചെയ്യും മുൻപ് പരിശോധിക്കേണ്ടവ' },
    excerpt: {
      en: 'A short checklist: dates, advance, cancellation terms, power backup, catering rules and what is included.',
      ml: 'തീയതി, അഡ്വാൻസ്, റദ്ദാക്കൽ നിബന്ധനകൾ, പവർ ബാക്കപ്പ്, ഭക്ഷണ നിയമങ്ങൾ — ഒരു ചെറിയ ചെക്ക്‌ലിസ്റ്റ്.',
    },
    body: [
      {
        p: 'Once you have found a hall you like, it is tempting to book it straight away. A few minutes with this checklist, before you send the request, avoids most of the surprises people run into later.',
      },
      { h: 'Dates and timing' },
      {
        list: [
          'The exact session you need: morning, afternoon, evening or the full day.',
          'When you can get in to decorate, and when you must leave.',
          'What happens if the event overruns, and how it is charged.',
          'Whether another event is booked in the same hall right before or after yours.',
        ],
      },
      { h: 'Money' },
      {
        list: [
          'The price for your session, and what it includes.',
          'The advance needed to confirm, and when the balance is due.',
          'Any separate charges: generator fuel, electricity, cleaning, extra chairs, stage or sound.',
          'A receipt for every payment you make.',
        ],
      },
      { h: 'Cancellation' },
      {
        p: 'Ask what you get back if you cancel, and how that changes as the date gets closer. Plans change for good reasons, and it is far easier to agree the terms now than to negotiate them later.',
      },
      {
        tip: 'On Aghosha the cancellation terms are the same for every booking: if you have paid the venue, you get everything back when you cancel 48 hours or more before the event, half between 12 and 48 hours, and nothing in the last 12 hours. The venue returns the money to you directly.',
      },
      { h: 'Facilities and rules' },
      {
        list: [
          'Power backup, and what it covers.',
          'Air conditioning in the areas you will use.',
          'Parking for cars and two-wheelers.',
          'Catering: in-house only, approved caterers, or your own.',
          'Decoration: what can be fixed to walls or ceilings, and whether open flames or fireworks are allowed.',
          'Sound: any limits on volume or a time when music must stop.',
          'Whether alcohol is permitted, if that matters for your event.',
        ],
      },
      { h: 'People and contacts' },
      {
        p: 'Get the name and number of the person who will be at the venue on the day, not only the person who took your booking. Ask whether a technician will be around for the sound and lights, and who to call if the power goes.',
      },
      { h: 'In writing' },
      {
        p: 'Whatever you agree on the phone, ask for it in a message or on the receipt: the session, the price, the advance, and anything the venue has promised to provide. When you book through Aghosha, your request, the venue\'s confirmation and the session you booked are kept in your bookings, and updates reach you by SMS or WhatsApp.',
      },
    ],
  },
  {
    slug: 'how-much-space-do-you-need',
    category: 'venues',
    photo: 'banquetRounds',
    readMins: 3,
    title: { en: 'How much space does your event need?', ml: 'നിങ്ങളുടെ പരിപാടിക്ക് എത്ര സ്ഥലം വേണം?' },
    excerpt: {
      en: 'Theatre, classroom or banquet seating change capacity a lot. A simple way to match guest count to hall size.',
      ml: 'തിയേറ്റർ, ക്ലാസ്റൂം, ബാങ്ക്വറ്റ് ഇരിപ്പിട രീതികൾ ശേഷിയെ ബാധിക്കും. അതിഥികളുടെ എണ്ണത്തിനനുസരിച്ച് ഹാൾ കണ്ടെത്താം.',
    },
    body: [
      {
        p: 'The capacity printed on a listing is a starting point, not a promise for your event. The same hall can seat very different numbers depending on how the chairs are arranged and what else needs floor space. Here is how to think it through.',
      },
      { h: 'Know which layout you need' },
      {
        list: [
          'Theatre: rows of chairs facing a stage. It fits the most people and suits ceremonies, talks and performances.',
          'Classroom: rows of tables with chairs on one side. Good for training and note-taking, and seats noticeably fewer than theatre style in the same room.',
          'Banquet: round tables with chairs all around. Good for receptions and dinners, where people eat and talk at the same table. It needs space between tables for serving.',
          'U-shape or boardroom: one table arrangement everyone faces into. Best for small groups who discuss rather than listen.',
          'Standing or cocktail: no fixed seating, a few high tables. The most people per square foot, but only for short events.',
        ],
      },
      {
        p: 'Ask the venue which number they quote and for which layout. A hall advertised for a large theatre audience may hold far fewer people at round tables.',
      },
      { h: 'Subtract what is not seating' },
      {
        p: 'Stages, buffet counters, a dance floor, a photo backdrop, sound and video desks, registration tables and walkways all take space from the floor. For a wedding with a large stage and a buffet inside the hall, the usable seating can shrink a lot. If the meal is served in a separate dining hall, the main hall can seat more.',
      },
      { h: 'Seated capacity and floating capacity' },
      {
        p: 'Many venues give two figures. Seated capacity is the number of chairs. Floating capacity is how many people can comfortably pass through over the event, as guests arrive, greet the hosts, eat and leave. For a reception or open house where few people stay for the whole time, floating capacity is the better guide. For a conference or a performance where everyone sits from start to finish, use the seated figure.',
      },
      {
        tip: 'Aim for a hall whose capacity for your layout is a little above your expected guest count. Some room to spare keeps aisles clear and the hall comfortable; a hall several times too big makes even a good turnout look thin.',
      },
      { h: 'Do not forget the dining hall' },
      {
        p: 'If food is served in a separate dining area, its capacity matters as much as the main hall\'s. Divide your guest count by the number of seats to see how many batches you will need, then decide whether the wait between batches is acceptable.',
      },
      { h: 'Checking a hall on Aghosha' },
      {
        p: 'Search with your guest count to see halls that can take it, and look at each listing\'s seating capacity and floating capacity where the venue has given one. When you send a request, mention your layout in the event details so the venue can tell you if it fits.',
      },
    ],
  },
  {
    slug: 'auditorium-vs-convention-centre',
    category: 'venues',
    photo: 'conventionHall',
    readMins: 3,
    title: { en: 'Auditorium or convention centre?', ml: 'ഓഡിറ്റോറിയമോ കൺവെൻഷൻ സെന്ററോ?' },
    excerpt: {
      en: 'Fixed seating or a flexible floor? What each kind of venue does well, and which events suit each.',
      ml: 'സ്ഥിര ഇരിപ്പിടമോ മാറ്റാവുന്ന തറയോ? ഓരോ വേദിയും ഏതു പരിപാടിക്ക് അനുയോജ്യമെന്ന് അറിയാം.',
    },
    body: [
      {
        p: 'The words are used loosely, and in Kerala many wedding halls are called auditoriums. When you compare venues, it helps to look past the name at how the space actually works: is the seating fixed and facing a stage, or is it an open floor you arrange yourself?',
      },
      { h: 'What an auditorium does well' },
      {
        p: 'A true auditorium is built for an audience watching a stage. Seating is usually fixed, often tiered or raked so the back rows can see over the front. The stage is permanent, with wings, lighting and a sound system designed for the room.',
      },
      {
        list: [
          'Cultural programmes, dance and music performances, and school or college annual days.',
          'Award ceremonies, public talks and religious discourses.',
          'Large seminars and conferences where everyone faces the speaker.',
        ],
      },
      {
        p: 'The trade-off is flexibility. You cannot set up round tables or a buffet among fixed seats, so meals happen elsewhere, and the hall can feel empty if your audience is much smaller than its seating.',
      },
      { h: 'What a convention centre does well' },
      {
        p: 'A convention centre is usually a large, flat, open floor, sometimes divisible into smaller halls, with a separate dining area and space for registration and exhibition. You bring in the layout you need.',
      },
      {
        list: [
          'Weddings and receptions with a stage, seating and a buffet in the same space.',
          'Corporate events that mix a keynote, breakout sessions and lunch.',
          'Exhibitions, trade fairs and product launches with stalls or displays.',
        ],
      },
      {
        p: 'The trade-off is that a flat floor needs a raised stage and good sound to work for an audience, and setup takes longer because everything is arranged for your event.',
      },
      { h: 'Wedding halls and banquet halls' },
      {
        p: 'Between the two sit the halls most families use: a main hall with a built-in stage and movable chairs, and a dining hall alongside. Banquet halls lean towards dining, with round tables and a smaller stage. Both are often the most practical choice for family functions.',
      },
      {
        tip: 'Ask yourself one question: will your guests mostly watch, or mostly mingle and eat? Watching points to an auditorium. Mingling and eating points to a convention centre, wedding hall or banquet hall.',
      },
      { h: 'Using categories on Aghosha' },
      {
        p: 'Every listing on Aghosha has a category: auditorium, wedding hall, banquet hall or convention centre. Browse by event type to see the categories that usually suit it, then read each listing for the stage, seating and dining details that matter for your event.',
      },
    ],
  },
  {
    slug: 'event-planning-checklist',
    category: 'planning',
    photo: 'tableStyling',
    readMins: 3,
    title: { en: 'Event planning checklist: three months to the day', ml: 'പരിപാടി ആസൂത്രണ ചെക്ക്‌ലിസ്റ്റ്: മൂന്ന് മാസം മുതൽ അന്ന് വരെ' },
    excerpt: {
      en: 'What to settle when — venue, vendors, invitations, rehearsals — so nothing is left to the last week.',
      ml: 'വേദി, സേവനദാതാക്കൾ, ക്ഷണം, റിഹേഴ്സൽ — ഓരോന്നും എപ്പോൾ തീരുമാനിക്കണം എന്ന് അറിയാം.',
    },
    body: [
      {
        p: 'Most events go wrong in the last week because something that should have been settled weeks earlier was left open. This timeline works for a wedding, a large family function or a company event. Move the steps earlier if your date falls in a busy season, when good halls are booked well ahead.',
      },
      { h: 'Three months before' },
      {
        list: [
          'Fix the date, and a second choice in case your first is taken.',
          'Agree a realistic guest count and a budget.',
          'Shortlist venues that fit your guest count and location, and check which are free on your date.',
          'Visit your top two or three, then book and pay the advance once the venue confirms.',
        ],
      },
      { h: 'Two months before' },
      {
        list: [
          'Book the caterer, if the venue allows outside catering, and agree the menu outline.',
          'Book the photographer and videographer, decorator, and sound and light team.',
          'For a conference, confirm speakers and build the agenda.',
          'Draft the invitation in the languages your guests read. Many Kerala families print in both Malayalam and English.',
        ],
      },
      { h: 'One month before' },
      {
        list: [
          'Send invitations, printed and on WhatsApp.',
          'Plan transport for guests coming from far away, and rooms if they will stay.',
          'Share your timings with every vendor, and the venue contact with all of them.',
          'Confirm with the venue when decorators can enter and when you must vacate.',
        ],
      },
      { h: 'Two weeks before' },
      {
        list: [
          'Get a firm guest count and give it to the caterer.',
          'Plan the seating for family and important guests, or the room layout for a conference.',
          'Pay vendor advances that are due, and keep the receipts together.',
          'Make a minute-by-minute schedule for the day.',
        ],
      },
      { h: 'The week of the event' },
      {
        list: [
          'Call the venue to confirm your session, the setup time and the power backup.',
          'Run a rehearsal for anything with timed entries, speeches or performances.',
          'Prepare a small kit: tape, scissors, safety pins, a phone charger, a first-aid box and some cash.',
          'Assign one person to deal with the venue and vendors on the day.',
        ],
      },
      {
        tip: 'Put every vendor\'s name, number and arrival time on a single page, and give a copy to the person in charge on the day. When the decorator is late or the generator needs starting, nobody has to search through a phone.',
      },
      { h: 'On the day' },
      {
        p: 'Arrive before the vendors do. Walk through the hall with the venue staff, check the stage, sound, lights and toilets, and confirm when the meal will be served. Then hand the details to your person in charge, and give yourself time to enjoy the event you planned.',
      },
      { h: 'After the event' },
      {
        p: 'Settle the venue balance and collect any deposit that is due back. If you booked through Aghosha, leaving a review helps the next family or organiser choosing a hall.',
      },
    ],
  },
  {
    slug: 'plan-a-corporate-conference',
    category: 'corporate',
    photo: 'conferenceStage',
    readMins: 3,
    title: { en: 'How to plan a corporate conference', ml: 'കോർപ്പറേറ്റ് കോൺഫറൻസ് എങ്ങനെ ആസൂത്രണം ചെയ്യാം' },
    excerpt: {
      en: 'Sessions, stage and AV, registration, breaks and meals — a practical plan for a one-day conference.',
      ml: 'സെഷനുകൾ, സ്റ്റേജ്, ശബ്ദസംവിധാനം, രജിസ്ട്രേഷൻ, ഇടവേളകൾ — ഒരു ദിവസത്തെ കോൺഫറൻസിനുള്ള പദ്ധതി.',
    },
    body: [
      {
        p: 'A good conference feels effortless to attend and runs to time. That comes from a clear agenda, a venue that suits it, and equipment that has been tested before the first speaker walks on stage.',
      },
      { h: 'Start with the agenda' },
      {
        p: 'Decide what the day must achieve, then build the programme around it: keynote, sessions, panels, breaks and meals. The agenda tells you what the venue needs. A single main hall is enough for a day of talks. Parallel tracks or workshops need breakout rooms. A product showcase needs floor space for displays.',
      },
      { h: 'Choosing the venue' },
      {
        list: [
          'Seated capacity in the layout you want, usually theatre style for keynotes and classroom style for working sessions.',
          'A separate area for registration, so arriving delegates do not crowd the hall entrance.',
          'Breakout rooms close to the main hall, if your agenda has them.',
          'A dining area away from the main hall, so the room can be reset during lunch.',
          'Parking, a lift if the hall is upstairs, and easy access from the nearest station or highway.',
          'Air conditioning and power backup that covers the hall and all the AV equipment.',
        ],
      },
      { h: 'Stage and AV' },
      {
        list: [
          'Projector or LED screen large enough to read from the back row.',
          'Enough wireless microphones for panels, plus a handheld for audience questions.',
          'A laptop connection at the podium, with the right cable and an adapter spare.',
          'Stage lighting that lights the speaker\'s face, not just the backdrop.',
          'A technician from the venue or your AV vendor present for the whole day.',
          'Internet access for presenters who need it, and a mobile hotspot as backup.',
        ],
      },
      {
        tip: 'Collect every presentation the day before, load them onto one laptop, and run through each one on the actual screen during setup. Most delays at conferences come from a file that will not open or a cable that does not fit.',
      },
      { h: 'Registration and arrival' },
      {
        p: 'Send joining details a few days ahead with the venue address, a map link, parking information and the start time. On the day, have printed lists and name badges ready, and enough people at the desk to clear the queue before the opening session.',
      },
      { h: 'Breaks and meals' },
      {
        p: 'Plan tea breaks every couple of hours and keep them short and on time. For lunch, agree the menu and the serving style with the caterer, include vegetarian options, and ask how quickly the venue can serve your full number. A buffet in a separate dining hall usually keeps the schedule better than a served meal in the conference hall.',
      },
      { h: 'After the conference' },
      {
        p: 'Thank speakers and sponsors, share presentations with delegates if you promised them, and ask for feedback while the day is fresh. Settle the venue balance and keep the receipts for your accounts.',
      },
      { h: 'Finding a hall on Aghosha' },
      {
        p: 'Browse conference and corporate venues by city and guest count, check which sessions are free on your date, and compare two halls side by side. Send a request with your event details. The venue confirms it, and you pay the venue directly.',
      },
    ],
  },
];

export const articleBySlug = (slug: string | undefined) => ARTICLES.find((a) => a.slug === slug);
